import { useState } from 'react';
import {
  Languages,
  Search,
  Plus,
  Edit2,
  Save,
  X,
  Check,
  Globe,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface Translation {
  id: string;
  key: string;
  tr: string;
  en: string;
  category: string;
  lastUpdated: Date;
}

// Demo data
const initialTranslations: Translation[] = [
  { id: '1', key: 'nav.home', tr: 'Ana Sayfa', en: 'Home', category: 'Navigation', lastUpdated: new Date() },
  { id: '2', key: 'nav.services', tr: 'Hizmetler', en: 'Services', category: 'Navigation', lastUpdated: new Date() },
  { id: '3', key: 'nav.products', tr: 'Urunler', en: 'Products', category: 'Navigation', lastUpdated: new Date() },
  { id: '4', key: 'nav.about', tr: 'Hakkimizda', en: 'About', category: 'Navigation', lastUpdated: new Date() },
  { id: '5', key: 'nav.contact', tr: 'Iletisim', en: 'Contact', category: 'Navigation', lastUpdated: new Date() },
  { id: '6', key: 'hero.title', tr: 'Dijital Donusumunuzu Gerceklestirin', en: 'Accelerate Your Digital Transformation', category: 'Hero', lastUpdated: new Date() },
  { id: '7', key: 'hero.subtitle', tr: 'Yenilikci yazilim cozumleri', en: 'Innovative software solutions', category: 'Hero', lastUpdated: new Date() },
  { id: '8', key: 'cta.contact', tr: 'Iletisime Gecin', en: 'Contact Us', category: 'CTA', lastUpdated: new Date() },
  { id: '9', key: 'cta.demo', tr: 'Demo Talep Et', en: 'Request Demo', category: 'CTA', lastUpdated: new Date() },
  { id: '10', key: 'footer.rights', tr: 'Tum haklari saklidir', en: 'All rights reserved', category: 'Footer', lastUpdated: new Date() },
];

const categories = ['Tumu', 'Navigation', 'Hero', 'CTA', 'Footer', 'Forms', 'Errors'];
const languages = [
  { code: 'tr', name: 'Turkce', flag: '🇹🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

export function AdminTranslations() {
  const [translations, setTranslations] = useState<Translation[]>(initialTranslations);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('Tumu');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ tr: string; en: string }>({ tr: '', en: '' });

  // Filter translations
  const filteredTranslations = translations.filter(t => {
    const matchesSearch = t.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.tr.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.en.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'Tumu' || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleEdit = (t: Translation) => {
    setEditingId(t.id);
    setEditValues({ tr: t.tr, en: t.en });
  };

  const handleSave = (id: string) => {
    setTranslations(translations.map(t =>
      t.id === id ? { ...t, tr: editValues.tr, en: editValues.en, lastUpdated: new Date() } : t
    ));
    setEditingId(null);
  };

  const stats = {
    total: translations.length,
    categories: new Set(translations.map(t => t.category)).size,
    missing: translations.filter(t => !t.tr || !t.en).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Ceviri Yonetimi</h1>
          <p className="text-text-secondary">Site iceriklerinin cevirilerini yonetin</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent/90 transition-colors">
          <Plus size={18} /> Yeni Ceviri
        </button>
      </div>

      {/* Language Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {languages.map(lang => (
          <div key={lang.code} className="bg-bg-card border border-border-default rounded-xl p-4 flex items-center gap-4">
            <span className="text-3xl">{lang.flag}</span>
            <div className="flex-1">
              <p className="font-medium text-text-primary">{lang.name}</p>
              <p className="text-sm text-text-secondary">{translations.length} ceviri</p>
            </div>
            <div className="flex items-center gap-1 text-green-500">
              <Check size={16} />
              <span className="text-sm">Aktif</span>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-accent">{stats.total}</p>
          <p className="text-sm text-text-secondary">Toplam Ceviri</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-purple-500">{stats.categories}</p>
          <p className="text-sm text-text-secondary">Kategori</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-500">{stats.missing}</p>
          <p className="text-sm text-text-secondary">Eksik</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
          <input
            type="text"
            placeholder="Anahtar veya metin ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Translations Table */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-bg-elevated">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-semibold text-text-tertiary uppercase">Anahtar</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-text-tertiary uppercase">
                <span className="flex items-center gap-1">🇹🇷 Turkce</span>
              </th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-text-tertiary uppercase">
                <span className="flex items-center gap-1">🇬🇧 English</span>
              </th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-text-tertiary uppercase">Kategori</th>
              <th className="text-right px-6 py-3 text-xs font-semibold text-text-tertiary uppercase">Islemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {filteredTranslations.map(t => (
              <tr key={t.id} className="hover:bg-bg-elevated/50">
                <td className="px-6 py-4">
                  <code className="px-2 py-1 rounded bg-bg-elevated text-sm text-accent">{t.key}</code>
                </td>
                <td className="px-6 py-4">
                  {editingId === t.id ? (
                    <input
                      type="text"
                      value={editValues.tr}
                      onChange={(e) => setEditValues({ ...editValues, tr: e.target.value })}
                      className="w-full px-3 py-1.5 rounded bg-bg-elevated border border-accent focus:outline-none text-sm"
                    />
                  ) : (
                    <span className="text-sm text-text-primary">{t.tr || <span className="text-yellow-500 italic">Eksik</span>}</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {editingId === t.id ? (
                    <input
                      type="text"
                      value={editValues.en}
                      onChange={(e) => setEditValues({ ...editValues, en: e.target.value })}
                      className="w-full px-3 py-1.5 rounded bg-bg-elevated border border-accent focus:outline-none text-sm"
                    />
                  ) : (
                    <span className="text-sm text-text-primary">{t.en || <span className="text-yellow-500 italic">Missing</span>}</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 rounded-full bg-purple-500/10 text-purple-500 text-xs">{t.category}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    {editingId === t.id ? (
                      <>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1.5 rounded hover:bg-bg-elevated text-text-tertiary"
                        >
                          <X size={16} />
                        </button>
                        <button
                          onClick={() => handleSave(t.id)}
                          className="p-1.5 rounded bg-accent text-white hover:bg-accent/90"
                        >
                          <Save size={16} />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleEdit(t)}
                        className="p-1.5 rounded hover:bg-bg-elevated text-text-tertiary hover:text-accent"
                      >
                        <Edit2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredTranslations.length === 0 && (
          <div className="text-center py-12">
            <Languages className="mx-auto mb-4 text-text-tertiary" size={48} />
            <p className="text-text-tertiary">Ceviri bulunamadi</p>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-blue-500 mt-0.5" size={20} />
          <div>
            <p className="font-medium text-blue-500 mb-1">Ceviri Anahtar Formati</p>
            <p className="text-sm text-blue-500/80">
              Anahtarlar nokta notasyonu ile kategorize edilir: <code className="bg-blue-500/20 px-1 rounded">kategori.alt_kategori.anahtar</code>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
